import { create } from 'zustand';
import type { ModelEdition } from '../components/common/ModelVersionControl';
import type { ImageRole, Model, Prompt } from '../types';

export type TryOnGarmentRole = Extract<ImageRole, 'top' | 'bottom' | 'inner'>;
export type TryOnOutfitSlotId = 'slot_1' | 'slot_2' | 'slot_3';
export type TryOnGenerateMode = 'normal' | 'outfit';

export interface TryOnOutfitSlot {
  id: TryOnOutfitSlotId;
  role: TryOnGarmentRole;
  filePath: string | null;
}

export interface TryOnOutfitSet {
  id: string;
  slots: TryOnOutfitSlot[];
}

const DEFAULT_TRYON_OUTFIT_SLOTS: TryOnOutfitSlot[] = [
  { id: 'slot_1', role: 'top', filePath: null },
  { id: 'slot_2', role: 'bottom', filePath: null },
  { id: 'slot_3', role: 'inner', filePath: null },
];

function createEmptyOutfitSet(id = createOutfitSetId()): TryOnOutfitSet {
  return {
    id,
    slots: DEFAULT_TRYON_OUTFIT_SLOTS.map((slot) => ({ ...slot })),
  };
}

function createOutfitSetId(): string {
  return `outfit_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

interface TryOnState {
  selectedModels: Model[];
  generateMode: TryOnGenerateMode;
  normalGarmentFiles: string[];
  outfitSets: TryOnOutfitSet[];
  stylePreset: string;
  selectedPrompt: Prompt | null;
  promptText: string;
  isPromptEditorOpen: boolean;
  customPromptText: string;
  isPromptDirty: boolean;
  customPromptSourceSelection: string | null;
  negativePrompt: string;
  selectedAspectKey: string;
  selectedResolution: string;
  selectedShotKey: string;
  activeTaskIds: string[];
  selectedModelEdition: ModelEdition;
  setSelectedModels: (models: Model[] | ((prev: Model[]) => Model[])) => void;
  setGenerateMode: (mode: TryOnGenerateMode) => void;
  setNormalGarmentFiles: (files: string[] | ((prev: string[]) => string[])) => void;
  setOutfitSlotRole: (setId: string, slotId: TryOnOutfitSlotId, role: TryOnGarmentRole) => void;
  setOutfitSlotFile: (setId: string, slotId: TryOnOutfitSlotId, filePath: string | null) => void;
  addOutfitSet: () => void;
  removeOutfitSet: (setId: string) => void;
  setStylePreset: (preset: string) => void;
  setSelectedPrompt: (prompt: Prompt | null) => void;
  setPromptText: (text: string) => void;
  setPromptEditorOpen: (open: boolean) => void;
  setCustomPromptText: (text: string, dirty?: boolean, sourceSelection?: string | null) => void;
  resetPromptEditor: () => void;
  setNegativePrompt: (text: string) => void;
  setSelectedAspectKey: (key: string) => void;
  setSelectedResolution: (res: string) => void;
  setSelectedShotKey: (key: string) => void;
  setActiveTaskIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  setSelectedModelEdition: (edition: ModelEdition) => void;
  toggleModel: (model: Model) => void;
  clearNormalGarmentFiles: () => void;
  clearOutfitFiles: () => void;
  reset: () => void;
}

export const useTryOnStore = create<TryOnState>((set) => ({
  selectedModels: [],
  generateMode: 'normal',
  normalGarmentFiles: [],
  outfitSets: [createEmptyOutfitSet('outfit_1')],
  stylePreset: 'white_studio',
  selectedPrompt: null,
  promptText: '',
  isPromptEditorOpen: false,
  customPromptText: '',
  isPromptDirty: false,
  customPromptSourceSelection: null,
  negativePrompt: '',
  selectedAspectKey: '1:1',
  selectedResolution: '1K',
  selectedShotKey: 'full',
  activeTaskIds: [],
  selectedModelEdition: 'auto',

  setSelectedModels: (models) =>
    set((state) => ({
      selectedModels: typeof models === 'function' ? models(state.selectedModels) : models,
    })),

  setGenerateMode: (generateMode) => set({ generateMode }),

  setNormalGarmentFiles: (files) =>
    set((state) => ({
      normalGarmentFiles: typeof files === 'function' ? files(state.normalGarmentFiles) : files,
    })),

  setOutfitSlotRole: (setId, slotId, role) =>
    set((state) => ({
      outfitSets: state.outfitSets.map((setItem) => {
        if (setItem.id !== setId) return setItem;
        const current = setItem.slots.find((item) => item.id === slotId);
        if (!current) return setItem;
        return {
          ...setItem,
          slots: setItem.slots.map((slot) => {
            if (slot.id === slotId) return { ...slot, role };
            if (slot.role === role) return { ...slot, role: current.role };
            return slot;
          }),
        };
      }),
    })),

  setOutfitSlotFile: (setId, slotId, filePath) =>
    set((state) => ({
      outfitSets: state.outfitSets.map((setItem) =>
        setItem.id === setId
          ? {
              ...setItem,
              slots: setItem.slots.map((slot) =>
                slot.id === slotId ? { ...slot, filePath } : slot
              ),
            }
          : setItem
      ),
    })),

  addOutfitSet: () =>
    set((state) => ({
      outfitSets: [...state.outfitSets, createEmptyOutfitSet()],
    })),

  removeOutfitSet: (setId) =>
    set((state) => {
      if (state.outfitSets.length <= 1) {
        return { outfitSets: [createEmptyOutfitSet('outfit_1')] };
      }
      return { outfitSets: state.outfitSets.filter((setItem) => setItem.id !== setId) };
    }),

  setStylePreset: (stylePreset) => set({ stylePreset }),
  setSelectedPrompt: (prompt) => set({ selectedPrompt: prompt }),
  setPromptText: (text) => set({ promptText: text }),
  setPromptEditorOpen: (isPromptEditorOpen) => set({ isPromptEditorOpen }),
  setCustomPromptText: (customPromptText, dirty = true, customPromptSourceSelection = null) =>
    set({ customPromptText, isPromptDirty: dirty, customPromptSourceSelection }),
  resetPromptEditor: () =>
    set({ customPromptText: '', isPromptDirty: false, customPromptSourceSelection: null, isPromptEditorOpen: false }),
  setNegativePrompt: (text) => set({ negativePrompt: text }),
  setSelectedAspectKey: (key) => set({ selectedAspectKey: key }),
  setSelectedResolution: (res) => set({ selectedResolution: res }),
  setSelectedShotKey: (key) => set({ selectedShotKey: key }),

  setActiveTaskIds: (ids) =>
    set((state) => ({
      activeTaskIds: typeof ids === 'function' ? ids(state.activeTaskIds) : ids,
    })),

  setSelectedModelEdition: (selectedModelEdition) => set({ selectedModelEdition }),

  toggleModel: (model) =>
    set((state) => {
      const exists = state.selectedModels.some((m) => m.id === model.id);
      if (exists) {
        return { selectedModels: state.selectedModels.filter((m) => m.id !== model.id) };
      }
      return { selectedModels: [...state.selectedModels, model] };
    }),

  clearNormalGarmentFiles: () => set({ normalGarmentFiles: [] }),

  clearOutfitFiles: () =>
    set({ outfitSets: [createEmptyOutfitSet('outfit_1')] }),

  reset: () => set({
    selectedModels: [], generateMode: 'normal', normalGarmentFiles: [], outfitSets: [createEmptyOutfitSet('outfit_1')],
    stylePreset: 'white_studio', selectedPrompt: null, promptText: '', isPromptEditorOpen: false,
    customPromptText: '', isPromptDirty: false, customPromptSourceSelection: null, negativePrompt: '', selectedAspectKey: '1:1',
    selectedResolution: '1K', selectedShotKey: 'full', activeTaskIds: [], selectedModelEdition: 'auto',
  }),
}));
